const REGIONAL = /Province|Territory|County|District|Island|National|Isle/;
const MAX_RESULTS = 8;

// Shared across instances so the JSON files are fetched only once per page.
let entriesPromise = null;

const kindOf = ({ handle, name }) => {
  if (handle.startsWith('ireland-')) return 'Irish family tartan';
  if (/ of wales/i.test(name)) return 'Welsh tartan';
  if (REGIONAL.test(name)) return 'Regional tartan';
  return 'Scottish clan tartan';
};

// Lowercase, drop accents, apostrophes and brackets, and treat "Mc" as "Mac".
const norm = (value) =>
  String(value)
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[’'`.]/g, '')
    .replace(/[()]/g, ' ')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .replace(/(^| )mc/g, '$1mac');

const levenshtein = (a, b) => {
  if (a === b) return 0;
  if (Math.abs(a.length - b.length) > 2) return 3;
  let prev = Array.from({ length: b.length + 1 }, (_, j) => j);
  for (let i = 1; i <= a.length; i++) {
    const cur = [i];
    for (let j = 1; j <= b.length; j++) {
      cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
    }
    prev = cur;
  }
  return prev[b.length];
};

// Lower is better; -1 means no match. Handles prefixes, "Mc"/"Mac" and small typos.
const score = (entry, nq) => {
  if (entry.n === nq) return 0;
  if (entry.words.length < 4 && entry.words.includes(nq)) return 0;
  if (entry.n.startsWith(nq)) return 1;
  if (entry.words.some((word) => word.startsWith(nq))) return 2;
  if (nq.length >= 3 && entry.n.includes(nq)) return 3;
  if (nq.length >= 4) {
    const allowed = nq.length >= 7 ? 2 : 1;
    const parts = [entry.n, ...entry.words];
    const best = Math.min(...parts.map((part) => levenshtein(part.slice(0, nq.length), nq)), levenshtein(entry.n, nq));
    if (best <= allowed) return 3 + best;
  }
  return -1;
};

const el = (tag, className, text) => {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
};

// "MacAlister (McAlister)" is one family spelled two ways, so show it once.
// Other brackets, such as "Murphy (Wexford)" or "Stewart (Stuart)", are kept.
const displayName = (name) =>
  name.replace(/\s*\(([^)]*)\)/, (match, inner) => (norm(inner) === norm(name.replace(/\s*\([^)]*\)/, '')) ? '' : match));

const IMAGE_PATH = '/cdn/shop/collections/';

const loadEntries = (indexUrl) => {
  if (entriesPromise) return entriesPromise;
  entriesPromise = fetch(indexUrl)
    .then((response) => response.json())
    .catch(() => [])
    .then((rows) =>
      rows.map(([name, handle, file]) => {
        const n = norm(name);
        return { name: displayName(name), handle, n, words: n.split(' '), kind: kindOf({ name, handle }), image: file ? `${IMAGE_PATH}${file}&width=96` : '' };
      })
    );
  return entriesPromise;
};

const initFinder = (root) => {
  if (root.dataset.ready) return;
  root.dataset.ready = '1';

  const form = root.querySelector('[data-tvsf-form]');
  const input = root.querySelector('[data-tvsf-input]');
  const list = root.querySelector('[data-tvsf-list]');
  const none = root.querySelector('[data-tvsf-none]');
  const status = root.querySelector('[data-tvsf-status]');
  const card = root.querySelector('.tvsf__card');
  const { index, request, collections, search: searchUrl, suggest } = root.dataset;

  let entries = [];
  let results = [];
  let active = -1;
  let timer = 0;
  let controller = null;
  let currentQuery = '';

  const load = () => loadEntries(index).then((loaded) => (entries = loaded));

  const search = (query) => {
    const nq = norm(query);
    if (nq.length < 2) return [];
    return entries
      .map((entry) => ({ entry, rank: score(entry, nq) }))
      .filter(({ rank }) => rank > -1)
      .sort((a, b) => a.rank - b.rank || a.entry.name.localeCompare(b.entry.name))
      .slice(0, MAX_RESULTS)
      .map(({ entry, rank }) => ({ entry: { ...entry, url: collections + entry.handle }, exact: rank === 0 }));
  };

  // Product suggestions come from Shopify's predictive search; failures are ignored.
  const fetchProducts = (query) => {
    if (controller) controller.abort();
    controller = new AbortController();
    const params = new URLSearchParams({ q: query, 'resources[type]': 'product', 'resources[limit]': '3' });
    return fetch(`${suggest}.json?${params}`, { signal: controller.signal })
      .then((response) => response.json())
      .then((data) =>
        (data.resources?.results?.products || []).map((product) => ({
          entry: { name: product.title, url: product.url, kind: 'Product', image: product.image ? `${product.image}${product.image.includes('?') ? '&' : '?'}width=96` : '' },
          product: true,
        }))
      )
      .catch(() => []);
  };

  const highlight = (name, query) => {
    const wrap = el('span', 'tvsf__opt-title text-base font-semibold');
    const lower = name.toLowerCase();
    const q = query.toLowerCase();
    const variants = [q, q.replace(/^mc/, 'mac'), q.replace(/^mac/, 'mc')];
    for (const variant of variants) {
      const at = variant ? lower.indexOf(variant) : -1;
      if (at > -1) {
        wrap.append(name.slice(0, at), el('mark', '', name.slice(at, at + variant.length)), name.slice(at + variant.length));
        return wrap;
      }
    }
    wrap.textContent = name;
    return wrap;
  };

  const close = () => {
    list.hidden = true;
    input.setAttribute('aria-expanded', 'false');
    input.removeAttribute('aria-activedescendant');
    active = -1;
  };

  const hideNone = () => {
    none.hidden = true;
    none.replaceChildren();
  };

  const showNone = (query) => {
    close();
    none.replaceChildren();
    const requestLink = el('a', '', 'Request your tartan');
    requestLink.href = `${request}${request.includes('?') ? '&' : '?'}name=${encodeURIComponent(query)}`;
    const searchLink = el('a', '', 'search the whole store');
    searchLink.href = `${searchUrl}?q=${encodeURIComponent(query)}&options%5Bprefix%5D=last`;
    const next = el('span');
    next.append(requestLink, ' and we’ll look for it, or ', searchLink, '.');
    none.append(el('strong', '', `We couldn’t find “${query}” yet.`), next);
    none.hidden = false;
    status.textContent = `No tartans found for ${query}`;
  };

  const addGroup = (label, items, offset) => {
    if (!items.length) return;
    if (label) list.append(el('li', 'tvsf__group text-xs font-bold uppercase', label));
    items.forEach(({ entry }, index) => {
      const item = el('li', 'tvsf__opt');
      item.id = `${input.id}-opt-${offset + index}`;
      item.setAttribute('role', 'option');
      item.setAttribute('aria-selected', 'false');
      const link = el('a', 'tvsf__opt-link flex items-center rounded');
      link.href = entry.url;
      link.tabIndex = -1;
      const thumb = el('span', 'tvsf__opt-thumb flex-none overflow-hidden rounded');
      if (entry.image) {
        const img = el('img', 'block w-full h-full object-cover');
        img.src = entry.image;
        img.alt = entry.name;
        img.width = 36;
        img.height = 36;
        img.loading = 'lazy';
        thumb.append(img);
      }
      const text = el('span', 'tvsf__opt-text grid');
      text.append(highlight(entry.name, currentQuery), el('span', 'tvsf__opt-kind text-xs', entry.kind));
      link.append(thumb, text);
      item.append(link);
      list.append(item);
    });
  };

  const render = (query, products = []) => {
    hideNone();
    currentQuery = query;
    results = [...search(query), ...products];
    list.replaceChildren();
    active = -1;
    if (!results.length) {
      if (norm(query).length >= 2) showNone(query);
      else close();
      return;
    }
    const tartans = results.filter((result) => !result.product);
    addGroup('', tartans, 0);
    addGroup('Products', products, tartans.length);
    list.hidden = false;
    input.setAttribute('aria-expanded', 'true');
    status.textContent = `${results.length} matching results. Use the up and down arrows, then Enter.`;
  };

  const move = (step) => {
    const options = list.querySelectorAll('[role="option"]');
    if (!options.length) return;
    if (active > -1) options[active].setAttribute('aria-selected', 'false');
    active = (active + step + options.length) % options.length;
    options[active].setAttribute('aria-selected', 'true');
    options[active].scrollIntoView({ block: 'nearest' });
    input.setAttribute('aria-activedescendant', options[active].id);
  };

  const go = ({ entry }) => {
    window.location.href = entry.url;
  };

  input.addEventListener('focus', load);
  input.addEventListener('input', () => {
    clearTimeout(timer);
    const query = input.value.trim();
    if (query.length < 2) {
      results = [];
      close();
      hideNone();
      return;
    }
    timer = setTimeout(() => {
      load().then(() => {
        render(query);
        fetchProducts(query).then((products) => {
          // Only add products if the shopper has not kept typing meanwhile.
          if (products.length && input.value.trim() === query) render(query, products);
        });
      });
    }, 120);
  });
  input.addEventListener('keydown', (event) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      move(1);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      move(-1);
    } else if (event.key === 'Escape') {
      close();
    }
  });

  form.addEventListener('submit', (event) => {
    event.preventDefault();
    const query = input.value.trim();
    if (!query) {
      input.focus();
      return;
    }
    load().then(() => {
      results = search(query);
      if (active > -1 && results[active]) return go(results[active]);
      if (!results.length) return showNone(query);
      const exact = results.filter((result) => result.exact);
      if (exact.length === 1) return go(exact[0]);
      if (results.length === 1) return go(results[0]);
      render(query);
    });
  });

  list.addEventListener('mousedown', (event) => event.preventDefault());
  document.addEventListener('click', (event) => {
    if (!card.contains(event.target)) close();
  });
};

const initAll = () => document.querySelectorAll('[data-tvsf]').forEach(initFinder);

initAll();
// Re-initialise when the theme editor adds or reloads the section.
document.addEventListener('shopify:section:load', initAll);
